/**
 * DataList — кастомний аналог deprecated antd List.
 *
 * Замінює: import { List } from 'antd'
 * Використовується натомість: import { DataList } from '@/Core/UI/DataList'
 *
 * Підтримувані пропси (сумісні з antd List API):
 *  - dataSource, renderItem, loading, locale
 *  - itemLayout, header, footer, loadMore
 *  - bordered, split, rowKey, size, className, style
 *
 * Точки розширення (кастомізація ззовні Core):
 *  - components  — підміна внутрішніх вузлів (item, empty, loadingWrapper)
 *  - renderEmpty — довільний JSX замість дефолтного Empty
 *  - renderLoading — довільний JSX замість дефолтного Spin
 *
 * Підкомпоненти:
 *  - DataList.Item  (аналог List.Item)
 */

import React, { type CSSProperties, type ReactNode } from 'react'
import { Empty, Spin } from 'antd'
import type { SpinProps } from 'antd'
import { DataListItem } from './DataListItem'
import { DataListContext, type DataListComponents } from './DataListContext'

import './DataList.css'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DataListLocale {
    emptyText?: ReactNode
}

/**
 * Підміна внутрішніх вузлів DataList.
 * @see DataListComponents (в DataListContext.tsx)
 */
export type { DataListComponents }

export interface DataListProps<T = any> {
    /** Масив даних для списку */
    dataSource?: T[]
    /** Рендер-функція для кожного елемента */
    renderItem?: (item: T, index: number) => ReactNode
    /** Стан завантаження — перекриває список спінером */
    loading?: boolean | SpinProps
    /** Локалізація (текст при порожньому стані) */
    locale?: DataListLocale
    /** Розташування елементів */
    itemLayout?: 'horizontal' | 'vertical'
    /** Вміст шапки списку */
    header?: ReactNode
    /** Вміст підвалу списку */
    footer?: ReactNode
    /** Кнопка / контент "Завантажити ще" */
    loadMore?: ReactNode
    /** Чи показувати рамку навколо списку */
    bordered?: boolean
    /** Чи показувати розділювач між елементами */
    split?: boolean
    /** Ключ унікальності елемента */
    rowKey?: keyof T | ((item: T) => React.Key)
    /** Розмір списку */
    size?: 'default' | 'large' | 'small'
    className?: string
    style?: CSSProperties
    children?: ReactNode
    /**
     * Підміна внутрішніх вузлів.
     * @see DataListComponents
     */
    components?: DataListComponents
    /**
     * Повністю довільний JSX для порожнього стану.
     * Має пріоритет над `locale.emptyText` та `components.empty`.
     */
    renderEmpty?: () => ReactNode
    /**
     * Повністю довільний JSX для стану завантаження.
     * Має пріоритет над `components.loadingWrapper`.
     * Отримує поточне значення `spinning`.
     */
    renderLoading?: (spinning: boolean) => ReactNode
}

// ─── Context (передає components вглиб дерева для DataList.Item) ──────────────
// Експортуємо з DataListContext.tsx, щоб уникнути циклічних імпортів
export { DataListContext, useDataListContext } from './DataListContext'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getItemKey<T>(item: T, index: number, rowKey?: DataListProps<T>['rowKey']): React.Key {
    if (!rowKey) return index
    if (typeof rowKey === 'function') return rowKey(item)
    return (item as any)[rowKey as string] ?? index
}

// ─── Default loading wrapper (antd Spin) ─────────────────────────────────────

const DefaultLoadingWrapper: React.FC<{ spinning: boolean; children: ReactNode }> = ({
    spinning,
    children,
}) => {
    return <Spin spinning={spinning}>{children}</Spin>
}

// ─── Component ────────────────────────────────────────────────────────────────

type DataListComponent = (<T = any>(props: DataListProps<T>) => ReactNode) & {
    Item: typeof DataListItem
}

const DataListInner = <T = any>({
    dataSource = [],
    renderItem,
    loading = false,
    locale,
    itemLayout = 'horizontal',
    header,
    footer,
    loadMore,
    bordered = false,
    split = true,
    rowKey,
    size = 'default',
    className = '',
    style,
    children,
    components,
    renderEmpty,
    renderLoading,
}: DataListProps<T>) => {
    const isLoading = typeof loading === 'boolean' ? loading : (loading as SpinProps).spinning ?? false

    const sizeClass = size !== 'default' ? `data-list--${size}` : ''
    const borderedClass = bordered ? 'data-list--bordered' : ''
    const splitClass = split ? 'data-list--split' : ''
    const layoutClass = `data-list--${itemLayout}`

    const rootClass = ['data-list', sizeClass, borderedClass, splitClass, layoutClass, className]
        .filter(Boolean)
        .join(' ')

    // ── Вибір Empty-компонента ──────────────────────────────────────────────
    // Пріоритет: renderEmpty > components.empty > locale.emptyText > default
    const resolveEmpty = (): ReactNode => {
        if (renderEmpty) {
            return <div className="data-list__empty">{renderEmpty()}</div>
        }
        if (components?.empty) {
            const EmptyComponent = components.empty
            return (
                <div className="data-list__empty">
                    <EmptyComponent />
                </div>
            )
        }
        const emptyText = locale?.emptyText ?? 'No Data'
        return (
            <div className="data-list__empty">
                <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        )
    }

    // ── Рендер вмісту списку ────────────────────────────────────────────────
    const renderContent = (): ReactNode => {
        if (dataSource.length === 0 && !isLoading) {
            return resolveEmpty()
        }

        if (renderItem) {
            return dataSource.map((item, index) => (
                <React.Fragment key={getItemKey(item, index, rowKey)}>
                    {renderItem(item, index)}
                </React.Fragment>
            ))
        }

        return children
    }

    // ── Вибір Loading-wrapper ───────────────────────────────────────────────
    // Пріоритет: renderLoading > components.loadingWrapper > default Spin
    const renderLoadingContent = (inner: ReactNode): ReactNode => {
        if (renderLoading) {
            // renderLoading повністю контролює рендер (включаючи inner)
            return (
                <>
                    {isLoading ? renderLoading(isLoading) : inner}
                </>
            )
        }
        const LoadingWrapper = components?.loadingWrapper ?? DefaultLoadingWrapper
        return <LoadingWrapper spinning={isLoading}>{inner}</LoadingWrapper>
    }

    const inner = (
        <div className={rootClass} style={style}>
            {header && <div className="data-list__header">{header}</div>}

            <ul className="data-list__items">
                {renderContent()}
            </ul>

            {loadMore && <div className="data-list__load-more">{loadMore}</div>}
            {footer && <div className="data-list__footer">{footer}</div>}
        </div>
    )

    return (
        <DataListContext.Provider value={{ components }}>
            {renderLoadingContent(inner)}
        </DataListContext.Provider>
    )
}

// Прикріплюємо Item як статичний підкомпонент
export const DataList = DataListInner as DataListComponent
DataList.Item = DataListItem
